import React, { useState, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, Transition } from "@headlessui/react";
import { ChevronLeft, Search, MoreVertical, Info } from "lucide-react";

import AttachedFeeSidepanel from "./case-folder/sidebars/AttachedFeeSidepanel";
import PaymentModal from "./case-folder/modals/PaymentModal";
import PaymentSuccessModal from "./case-folder/modals/PaymentSuccessModal";
import PaymentReceiptModal from "./case-folder/modals/PaymentReceiptModal";
import { CLOSED_EYE, RED_BIN, ZOOM } from "../../../assets";
import SuccessModalV2 from "../../../shared/components/successModal/sucessModalV2";
import SubmitTransferModal from "./case-folder/modals/SubmitTransferModal";
import SetupMeetingModal from "./case-folder/modals/SetupMeetingModal";
import WarningPromptModal from "./case-folder/modals/WarningPromptModal";

const DOCUMENTS = [
  {
    id: 1,
    name: "companies_demo_export.xlsx",
    date: "2021-11-04 11:54",
    type: "xlsx",
    color: "bg-[#00B074]", // Green
  },
  {
    id: 2,
    name: "demo_image.jpg",
    date: "2021-11-03 22:00",
    type: "jpg",
    color: "bg-[#7F56D9]", // Purple
  },
  {
    id: 3,
    name: "sample_demo_export.xlsx",
    date: "2021-11-02 11:09",
    type: "xlsx",
    color: "bg-[#7F56D9]", // Purple
  },
  {
    id: 4,
    name: "visit_demo_export.xlsx",
    date: "2021-10-31 17:24",
    type: "xlsx",
    color: "bg-[#7F56D9]", // Purple
  },
];

const ViewJoinderFolder = () => {
  const navigate = useNavigate();

  const [serviceSuccessModal, setServiceSuccessModal] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [meetingSuccessModal, setMeetingSuccessModal] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [isFeeSidepanelOpen, setIsFeeSidepanelOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isPaymentSuccessModalOpen, setIsPaymentSuccessModalOpen] =
    useState(false);
  const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false);

  const [showInfo, setShowInfo] = useState(false);
  const [showGrantConfirm, setShowGrantConfirm] = useState(false);
  const [showGrantSuccess, setShowGrantSuccess] = useState(false);
  const [showDenyConfirm, setShowDenyConfirm] = useState(false);
  const [showDenySuccess, setShowDenySuccess] = useState(false);

  const handlePay = () => {
    setIsPaymentModalOpen(false);
    setIsPaymentSuccessModalOpen(true);
  };

  const handleViewReceipt = () => {
    setIsPaymentSuccessModalOpen(false);
    setIsReceiptModalOpen(true);
  };

  const handleGrantApplicationClick = () => {
    setShowInfo(false);
    setShowGrantConfirm(true);
  };

  const handleDenyApplicationClick = () => {
    setShowInfo(false);
    setShowDenyConfirm(true);
  };

  return (
    <div className="flex flex-col w-full h-full overflow-y-auto  min-h-screen">
      <AttachedFeeSidepanel
        isOpen={isFeeSidepanelOpen}
        onClose={() => setIsFeeSidepanelOpen(false)}
        onPay={() => {
          setIsFeeSidepanelOpen(false);
          setIsPaymentModalOpen(true);
        }}
      />
      <PaymentModal
        open={isPaymentModalOpen}
        close={() => setIsPaymentModalOpen(false)}
        onPay={handlePay}
      />
      <PaymentSuccessModal
        open={isPaymentSuccessModalOpen}
        close={() => setIsPaymentSuccessModalOpen(false)}
        onViewReceipt={handleViewReceipt}
      />
      <PaymentReceiptModal
        open={isReceiptModalOpen}
        close={() => setIsReceiptModalOpen(false)}
      />
      {/* Header & Breadcrumbs */}
      <div className="bg-white border-b border-[#E9EAEB] px-8 py-4 sticky top-0 z-10">
        <div className="flex items-center gap-x-2 text-sm text-[#475467] mb-6">
          <button
            onClick={() => navigate("/lawyer/case-folders")}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span
            onClick={() => navigate("/lawyer/case-folders")}
            className="cursor-pointer hover:text-[#1A1A1A] transition-colors"
          >
            Case folders
          </span>
          <span className="text-[#D0D5DD]">{">"}</span>
          <span className="font-medium text-[#1A1A1A]">
            Olugbenga Akintola vs Israel Anyamelu
          </span>
        </div>

        <div className="flex items-center justify-between">
          <h1 className="text-[28px] font-bold text-[#1A1A1A]">
            Olugbenga Akintola vs Israel Anyamelu
          </h1>
          <div className="flex items-center gap-x-4">
            <button className="px-6 py-2 text-nowrap rounded-full bg-[#00B074] text-white text-sm font-semibold hover:bg-[#009060] transition-colors">
              View Transactions
            </button>
            <button
              onClick={() => setShowMeetingModal(true)}
              className="px-6 py-2 rounded-full bg-[#00B074] text-white text-sm font-semibold flex items-center gap-x-2 hover:bg-[#009060] transition-colors min-w-fit flex-nowrap text-nowrap"
            >
              <img src={ZOOM} className="w-[21px] h-[21px]" alt="" />
              <span>Schedule a call</span>
            </button>
          </div>
        </div>
      </div>

      <div className="px-8 py-8 flex flex-col gap-y-8">
        <div className="w-full max-w-[264px] relative">
          <input
            type="text"
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-[#D0D5DD] rounded-lg text-sm placeholder:text-[#667085] focus:outline-none focus:border-[#00B074] focus:ring-1 focus:ring-[#00B074] shadow-sm"
          />
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#667085]" />
        </div>

        <div className="flex flex-col gap-y-3">
          {DOCUMENTS.map((doc) => (
            <div
              key={doc.id}
              className="bg-white border border-[#E9EAEB] rounded-xl p-4 flex items-center justify-between hover:shadow-sm transition-shadow"
            >
              <div className="flex items-center gap-x-4 flex-1">
                <div
                  className={`w-10 h-10 rounded-full ${doc.color} flex items-center justify-center text-white font-medium text-sm`}
                >
                  {doc.name.charAt(0).toUpperCase()}
                </div>

                <div className="flex flex-col flex-1 min-w-0">
                  <span className="text-sm font-medium text-[#101828] truncate">
                    {doc.name}
                  </span>
                </div>

                <div className="text-sm text-[#475467] w-40">{doc.date}</div>
              </div>

              <div className="flex items-center gap-x-4">
                <button className="flex items-center gap-x-2 text-[#027A48] hover:text-[#026aa2] px-3 py-2 rounded-lg transition-colors">
                  <img src={CLOSED_EYE} className="w-[24px] h-[24px]" alt="" />
                  <span className="text-sm font-medium">View</span>
                </button>

                <button className="flex items-center gap-x-2 bg-[#ECFDF3] text-[#027A48] px-4 py-2 rounded-full text-sm font-medium hover:bg-[#d1fadf] transition-colors">
                  Download
                </button>

                <Menu as="div" className="relative inline-block text-left">
                  <Menu.Button className="p-2 text-[#98A2B3] hover:text-[#475467] transition-colors focus:outline-none">
                    <MoreVertical className="w-5 h-5" />
                  </Menu.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 mt-2 w-48 origin-top-right divide-y divide-gray-100 rounded-xl bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50 py-2">
                      <div className="px-1 py-1 flex flex-col gap-y-1">
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              className={`${
                                active ? "bg-gray-50" : ""
                              } group flex w-full items-center gap-x-3 rounded-lg px-4 py-2.5 text-sm font-medium text-[#344054]`}
                            >
                              View
                            </button>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              className={`${
                                active ? "bg-gray-50" : ""
                              } group flex w-full items-center gap-x-3 rounded-lg px-4 py-2.5 text-sm font-medium text-[#344054]`}
                            >
                              Details
                            </button>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={() => setServiceSuccessModal(true)}
                              className={`${
                                active
                                  ? "bg-[#E6F7F1] text-[#00B074]"
                                  : "text-[#344054]"
                              } group flex w-full items-center gap-x-3 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors`}
                            >
                              Mark for service
                            </button>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              className={`${
                                active
                                  ? "bg-[#E6F7F1] text-[#00B074]"
                                  : "text-[#344054]"
                              } group flex w-full items-center gap-x-3 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors`}
                            >
                              Sign and stamp
                            </button>
                          )}
                        </Menu.Item>
                      </div>
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 flex justify-end items-end">
          <div className="relative flex items-center gap-x-4">
            {showInfo && (
              <div className="absolute bottom-full mb-3 right-0 w-[360px]">
                <div className="relative">
                  <div className="rounded-2xl bg-[#F9FAFB] border border-[#E5E7EB] shadow-lg px-5 py-4 text-xs text-[#111827]">
                    <p className="text-sm font-semibold mb-2">
                      Either of these actions closes this join request.
                    </p>
                    <p className="mb-1">
                      <span className="font-semibold">Grant application:</span>{" "}
                      use this button when the application has been granted to
                      close this join request.
                    </p>
                    <p>
                      <span className="font-semibold">Deny application:</span>{" "}
                      use this button when the application refused to close this
                      join request.
                    </p>
                  </div>
                  <div className="absolute right-10 top-full -mt-1">
                    <div className="w-3 h-3 bg-[#F9FAFB] border-r border-b border-[#E5E7EB] rotate-45" />
                  </div>
                </div>
              </div>
            )}

            <button
              onClick={handleGrantApplicationClick}
              className="min-w-[260px] px-10 py-3 rounded-md bg-[#00B074] text-white text-[16px] font-semibold hover:bg-[#009060] transition-colors"
            >
              Grant application
            </button>
            <button
              onClick={handleDenyApplicationClick}
              className="min-w-[260px] px-10 py-3 rounded-md bg-[#E5E5E5] text-[#111827] text-[16px] font-semibold hover:bg-[#D4D4D4] transition-colors"
            >
              Deny application
            </button>
            <button
              type="button"
              onClick={() => setShowInfo((prev) => !prev)}
              className="w-10 h-10 flex items-center justify-center rounded-full border border-[#111827] text-[#111827] hover:bg-[#F5F5F5] transition-colors"
            >
              <Info className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
      <WarningPromptModal
        isOpen={showGrantConfirm}
        onClose={() => setShowGrantConfirm(false)}
        message="Are you sure you want to grant this join application?"
        onClick={() => {
          setShowGrantConfirm(false);
          setShowGrantSuccess(true);
        }}
      />
      <WarningPromptModal
        isOpen={showDenyConfirm}
        onClose={() => setShowDenyConfirm(false)}
        message="Are you sure you want to refuse this application?"
        onClick={() => {
          setShowDenyConfirm(false);
          setShowDenySuccess(true);
        }}
      />
      <SuccessModalV2
        open={showGrantSuccess}
        close={() => setShowGrantSuccess(false)}
        message="Join application granted successfully"
      />
      <SuccessModalV2
        open={showDenySuccess}
        close={() => setShowDenySuccess(false)}
        message="Join application denied successfully"
      />
      <SuccessModalV2
        open={serviceSuccessModal}
        close={() => setServiceSuccessModal(false)}
        message={"Submitted for service Successfully"}
      />
      <SubmitTransferModal
        isOpen={showSubmitModal}
        onClose={() => setShowSubmitModal(false)}
        onClick={() => {
          setShowSubmitModal(false);
          setServiceSuccessModal(true);
        }}
      />
      <SetupMeetingModal
        isOpen={showMeetingModal}
        onClose={() => setShowMeetingModal(false)}
        onSubmit={() => {
          setShowMeetingModal(false);
          setMeetingSuccessModal(true);
        }}
      />
      <SuccessModalV2
        open={meetingSuccessModal}
        close={() => setMeetingSuccessModal(false)}
        message="Meeting setup successfully "
      />
    </div>
  );
};

export default ViewJoinderFolder;
